import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ninerBreadthService } from '../services/ninerBreadthService';
import { profileService } from '../services/profileService';
import type { FilterType, NinerBreadthFilters, ActiveFilter } from '../types/ninerBreadth';

interface NinerBreadthWidgetProps {
  windowId: string;
  onRemove: () => void;
}

const DEFAULT_TICKERS = 'SPX,SPY,QQQ,DIA,IWM';

const FILTER_LABELS: Record<FilterType, string> = {
  tickers: 'Tickers',
  asset_type: 'Asset Type',
  exchange: 'Exchange',
  sector: 'Sector',
  sub_sector: 'Sub-Sector',
};

export const NinerBreadthWidget: React.FC<NinerBreadthWidgetProps> = ({ windowId, onRemove }) => {
  // Filter state
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>({
    type: 'tickers',
    value: DEFAULT_TICKERS,
  });
  const [tickerInput, setTickerInput] = useState(DEFAULT_TICKERS);
  const [isStateLoaded, setIsStateLoaded] = useState(false);

  // Save state debounce ref
  const saveStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load saved state from backend
  useEffect(() => {
    const loadState = async () => {
      try {
        const response = await profileService.getFrontendState();
        const savedState = response.frontend_state?.ninerBreadthWidgets?.[windowId];
        if (savedState?.activeFilter) {
          setActiveFilter(savedState.activeFilter);
          if (savedState.activeFilter.type === 'tickers') {
            setTickerInput(savedState.activeFilter.value);
          }
        }
      } catch (error) {
        console.error('Failed to load niner breadth widget state:', error);
      } finally {
        setIsStateLoaded(true);
      }
    };
    loadState();
  }, [windowId]);

  // Save state to backend (debounced)
  const saveState = useCallback(() => {
    if (!isStateLoaded) return;

    if (saveStateTimeoutRef.current) {
      clearTimeout(saveStateTimeoutRef.current);
    }

    saveStateTimeoutRef.current = setTimeout(async () => {
      try {
        // Backend uses deep merge - only send this widget's state
        await profileService.saveFrontendState({
          ninerBreadthWidgets: {
            [windowId]: {
              activeFilter,
            },
          },
        });
      } catch (error) {
        console.error('Failed to save niner breadth widget state:', error);
      }
    }, 1000);
  }, [activeFilter, windowId, isStateLoaded]);

  useEffect(() => {
    saveState();
  }, [activeFilter, saveState]);

  // Fetch filter options
  const { data: filters } = useQuery({
    queryKey: ['ninerBreadthFilters'],
    queryFn: () => ninerBreadthService.fetchFilters(),
    staleTime: 60 * 60 * 1000, // 1 hour - these don't change often
  });

  // Fetch aggregate data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ninerBreadthAggregate', activeFilter.type, activeFilter.value],
    queryFn: () => ninerBreadthService.fetchAggregate(activeFilter.type, activeFilter.value),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  // Handle filter type change
  const handleFilterTypeChange = (newType: FilterType) => {
    if (newType === 'tickers') {
      setActiveFilter({ type: 'tickers', value: tickerInput || DEFAULT_TICKERS });
    } else {
      // Set first available option for the new filter type
      const options = getFilterOptions(newType, filters);
      setActiveFilter({ type: newType, value: options[0] || '' });
    }
  };

  // Handle filter value change
  const handleFilterValueChange = (value: string) => {
    if (activeFilter.type === 'tickers') {
      setTickerInput(value);
    }
    setActiveFilter((prev) => ({ ...prev, value }));
  };

  // Handle ticker input submit
  const handleTickerSubmit = () => {
    if (tickerInput.trim()) {
      setActiveFilter({ type: 'tickers', value: tickerInput.trim().toUpperCase() });
    }
  };

  // Get filter options based on type
  const getFilterOptions = (type: FilterType, filterData?: NinerBreadthFilters): string[] => {
    if (!filterData) return [];
    switch (type) {
      case 'asset_type':
        return filterData.asset_types;
      case 'exchange':
        return filterData.exchanges;
      case 'sector':
        return filterData.sectors;
      case 'sub_sector':
        return filterData.sub_sectors;
      default:
        return [];
    }
  };

  // Calculate gauge position (-2.5 to 2.5 range)
  // INVERTED: negative = bullish (bull-blue/right), positive = bearish (bear-red/left)
  const zScore = data?.z_scores?.net_setup_score?.value ?? 0;
  const clampedZScore = Math.max(-2.5, Math.min(2.5, zScore));
  // Map -2.5 to 2.5 => 0 to 100 (but inverted: -2.5 = 100%, 2.5 = 0%)
  const normalizedValue = ((clampedZScore + 2.5) / 5) * 100;
  // Invert for gauge: negative (bullish) on the right (bull-blue), positive (bearish) on the left (bear-red)
  const gaugeValue = 100 - normalizedValue;

  // Gauge SVG calculations
  const radius = 45;
  const strokeWidth = 8;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const arcLength = (circumference * 180) / 360;

  // Needle position (semicircle from left to right)
  const angle = 180 + (gaugeValue / 100) * 180;
  const angleRad = (angle * Math.PI) / 180;
  const needleLength = normalizedRadius - 5;
  const needleX = 55 + needleLength * Math.cos(angleRad);
  const needleY = 55 + needleLength * Math.sin(angleRad);

  // Get color based on z-score (inverted: negative = bull-blue, positive = bear-red)
  const getZScoreColor = (value: number) => {
    if (value < -1) return 'text-bull-blue';
    if (value > 1) return 'text-bear-red';
    return 'text-accent-yellow';
  };

  if (isLoading && !data) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Niner Breadth</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Niner Breadth</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-bear-red text-sm">Failed to load data</div>
        </div>
      </div>
    );
  }

  const percentile = data?.z_scores?.net_setup_score?.percentile ?? 0;
  const interpretation = data?.z_scores?.net_setup_score?.interpretation ?? '';
  const breadth = data?.breadth;
  const lookbackDays = data?.historical_lookback_days ?? 180;

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Niner Breadth</h3>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-2 px-2 py-1.5 bg-dark-bg/50 border-b border-dark-border relative z-20">
        {/* Filter Type Dropdown */}
        <select
          value={activeFilter.type}
          onChange={(e) => handleFilterTypeChange(e.target.value as FilterType)}
          className="text-xs bg-dark-surface border border-dark-border rounded px-1.5 py-1 text-gray-300 focus:outline-none focus:border-accent-yellow"
        >
          {Object.entries(FILTER_LABELS).map(([type, label]) => (
            <option key={type} value={type}>
              {label}
            </option>
          ))}
        </select>

        {/* Filter Value Input/Dropdown */}
        {activeFilter.type === 'tickers' ? (
          <div className="flex-1 flex items-center gap-1">
            <input
              type="text"
              value={tickerInput}
              onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleTickerSubmit()}
              placeholder="SPX,SPY,QQQ"
              className="flex-1 text-xs bg-dark-surface border border-dark-border rounded px-1.5 py-1 text-gray-300 placeholder-gray-500 focus:outline-none focus:border-accent-yellow min-w-0"
            />
            <button
              onClick={handleTickerSubmit}
              className="text-xs px-2 py-1 bg-dark-bg border border-dark-border rounded text-gray-300 hover:bg-dark-surface-light transition-colors"
            >
              Go
            </button>
          </div>
        ) : (
          <select
            value={activeFilter.value}
            onChange={(e) => handleFilterValueChange(e.target.value)}
            className="flex-1 text-xs bg-dark-surface border border-dark-border rounded px-1.5 py-1 text-gray-300 focus:outline-none focus:border-accent-yellow min-w-0"
          >
            {getFilterOptions(activeFilter.type, filters).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}

        {/* Refresh Button */}
        <button
          onClick={() => refetch()}
          className="text-gray-400 hover:text-white p-1 rounded hover:bg-dark-surface-light transition-colors"
          title="Refresh"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Close Button */}
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Z-Score Section with Gauge */}
        <div className="px-2 pt-2">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Niners Z-Score
          </div>

          {/* Gauge */}
          <div className="flex justify-center">
            <svg className="w-24 h-14" viewBox="0 0 110 70" preserveAspectRatio="xMidYMid meet">
              <defs>
                {/* Inverted gradient: bear-red on left (bearish/positive), bull-blue on right (bullish/negative) */}
                <linearGradient id={`niner-gauge-gradient-${windowId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#D8483B" />
                  <stop offset="50%" stopColor="#C2A14E" />
                  <stop offset="100%" stopColor="#1C76A2" />
                </linearGradient>
              </defs>

              {/* Background arc */}
              <circle
                cx="55"
                cy="55"
                r={normalizedRadius}
                fill="none"
                stroke="#2B2B2B"
                strokeWidth={strokeWidth}
                strokeDasharray={`${arcLength} ${circumference}`}
                strokeLinecap="round"
                transform="rotate(180 55 55)"
              />

              {/* Gradient gauge arc */}
              <circle
                cx="55"
                cy="55"
                r={normalizedRadius}
                fill="none"
                stroke={`url(#niner-gauge-gradient-${windowId})`}
                strokeWidth={strokeWidth}
                strokeDasharray={`${arcLength} ${circumference}`}
                strokeLinecap="round"
                transform="rotate(180 55 55)"
              />

              {/* Needle */}
              <line
                x1="55"
                y1="55"
                x2={needleX}
                y2={needleY}
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                className="transition-all duration-500"
              />

              {/* Needle center dot */}
              <circle cx="55" cy="55" r="3" fill="white" />

              {/* Scale labels */}
              <text x="8" y="58" className="text-[8px] fill-gray-500">-2.5</text>
              <text x="95" y="58" className="text-[8px] fill-gray-500">2.5</text>
            </svg>
          </div>

          {/* Z-Score Value and Percentile */}
          <div className="text-center -mt-1">
            <div className={`text-lg font-bold ${getZScoreColor(zScore)}`}>
              {zScore.toFixed(2)}
            </div>
            <div className="text-xs text-gray-400">
              {percentile.toFixed(1)}% percentile
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5 px-2 truncate" title={interpretation}>
              {interpretation}
            </div>
          </div>
        </div>

        {/* Breadth Section */}
        <div className="px-2 pt-2 border-t border-dark-border mt-2">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Breadth
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs">
            <div className="text-gray-400">Total:</div>
            <div className="text-gray-200 text-right">{breadth?.total_symbols ?? '-'}</div>

            <div className="text-gray-400">Buy Setup:</div>
            <div className="text-bull-blue text-right">{breadth?.buy_setup_pct?.toFixed(1) ?? '-'}%</div>

            <div className="text-gray-400">Sell Setup:</div>
            <div className="text-bear-red text-right">{breadth?.sell_setup_pct?.toFixed(1) ?? '-'}%</div>
          </div>
        </div>

        {/* Countdowns Section */}
        <div className="px-2 pt-2 border-t border-dark-border mt-2">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Countdowns
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs">
            <div className="text-gray-400">Buy Active:</div>
            <div className="text-bull-blue text-right">{breadth?.buy_countdown_active ?? '-'}</div>

            <div className="text-gray-400">Sell Active:</div>
            <div className="text-bear-red text-right">{breadth?.sell_countdown_active ?? '-'}</div>
          </div>
        </div>

        {/* Completed Section */}
        <div className="px-2 pt-2 border-t border-dark-border mt-2">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Completed
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs">
            <div className="text-gray-400">Buy (9+13):</div>
            <div className="text-bull-blue text-right">
              {(breadth?.completed_buy_9 ?? 0) + (breadth?.completed_buy_13 ?? 0)}
            </div>

            <div className="text-gray-400">Sell (9+13):</div>
            <div className="text-bear-red text-right">
              {(breadth?.completed_sell_9 ?? 0) + (breadth?.completed_sell_13 ?? 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-2 py-1 border-t border-dark-border bg-dark-bg/30 text-[9px] text-gray-500 flex justify-between">
        <span>{data?.market_date ?? '-'}</span>
        <span>Lookback: {lookbackDays} days</span>
      </div>
    </div>
  );
};
